import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useIntakeStore } from '@/hooks/useIntakeStore';
import { useAuthStore } from '@/store/authStore';

const SETUP_FEE_DISPLAY = import.meta.env.VITE_SETUP_FEE_DISPLAY as string ?? '$499';
const BUILD_TURNAROUND_DAYS = 5;

export function UpgradePrompt() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { setTrack, prefillFromProfile } = useIntakeStore();
  const { email } = useAuthStore();

  function handleConfirm() {
    // Pre-fill intake store with existing account data
    prefillFromProfile({
      email: email ?? '',
    });
    setTrack('staff');
    setOpen(false);
    // Route directly to summary — data already collected from profile
    navigate('/start/summary');
  }

  return (
    <>
      {/* Sidebar prompt card */}
      <div className="mx-3 mb-3 rounded-lg border border-brand-lavender bg-brand-surface p-3">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-brand-plum" />
          <p className="text-xs font-semibold text-brand-midnight">Want a pro touch?</p>
        </div>
        <p className="text-xs text-brand-ink mb-2">Have us finish your site</p>
        <Button
          size="sm"
          variant="outline"
          className="w-full text-xs border-brand-plum text-brand-plum hover:bg-brand-lilac"
          onClick={() => setOpen(true)}
        >
          Learn More
        </Button>
      </div>

      {/* Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-brand-midnight">Have Bread List finish your site</DialogTitle>
            <DialogDescription className="text-brand-ink">
              Our team takes your existing setup and polishes every detail.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-brand-surface border border-brand-border p-4 space-y-3">
              <div>
                <p className="text-sm font-semibold text-brand-midnight mb-1">What we do</p>
                <ul className="space-y-1 text-sm text-brand-ink">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-brand-plum flex-shrink-0" />
                    Professional copywriting for every page section
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-brand-plum flex-shrink-0" />
                    Custom brand styling — colors, fonts, and layout
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-brand-plum flex-shrink-0" />
                    MLS connection and listing configuration
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-brand-plum flex-shrink-0" />
                    One round of revisions included
                  </li>
                </ul>
              </div>

              <div className="flex items-center justify-between border-t border-brand-border pt-3">
                <div>
                  <p className="text-sm font-semibold text-brand-midnight">One-time setup fee</p>
                  <p className="text-xs text-brand-ink">Monthly plan continues unchanged</p>
                </div>
                <p className="text-2xl font-bold text-brand-plum">{SETUP_FEE_DISPLAY}</p>
              </div>

              <div className="flex items-center gap-2 text-xs text-brand-teal">
                <span>Ready within {BUILD_TURNAROUND_DAYS} business days</span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Not now
            </Button>
            <Button
              className="bg-brand-plum hover:bg-brand-plum-deep text-white"
              onClick={handleConfirm}
            >
              Yes, have Bread List finish my site →
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
